import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { useDraftCompaction, useConfirmCompaction } from "../../hooks/useCompaction";

export function CompactChatDialog({ open, onOpenChange, conversationId, onCompacted }) {
  const [summary, setSummary] = useState("");
  const draftCompaction = useDraftCompaction();
  const confirmCompaction = useConfirmCompaction();

  useEffect(() => {
    if (open && conversationId) {
      setSummary("");
      draftCompaction.mutate(conversationId, {
        onSuccess: (result) => setSummary(result.summary),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, conversationId]);

  const handleOpenChange = (next) => {
    if (!next) setSummary("");
    onOpenChange(next);
  };

  const handleConfirm = async () => {
    const result = await confirmCompaction.mutateAsync({ conversationId, summary: summary.trim() });
    handleOpenChange(false);
    onCompacted(result.context_usage);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="compact-chat-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Compactar chat</DialogTitle>
          <DialogDescription>
            Se resume todo lo conversado hasta ahora. Puedes editar el resumen antes de aplicarlo — el
            historial completo sigue visible arriba, pero de aquí en adelante el chat sigue con este
            resumen en vez de todo el texto.
          </DialogDescription>
        </DialogHeader>

        {draftCompaction.isPending ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
            <Loader2 className="h-4 w-4 animate-spin" /> Generando resumen...
          </div>
        ) : (
          <Textarea
            data-testid="compaction-summary-textarea"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            className="min-h-[220px] font-mono text-xs"
          />
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            data-testid="compaction-confirm-button"
            onClick={handleConfirm}
            disabled={draftCompaction.isPending || !summary.trim() || confirmCompaction.isPending}
          >
            {confirmCompaction.isPending ? "Aplicando..." : "Compactar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
